<template lang="pug">
//- The demo reply bubble, rendered once per scenario in the sizer (which fixes
//- the chat window's height) and live once the animation reaches the reply
//- step. `expr` is the template expression holding the scenario object ('cur'
//- for the live copy, 's' inside the sizer's v-for), spliced in at compile
//- time. The reply's intro is before + strong + mid + folder + ":", so each
//- scenario controls its own phrasing while the markup stays one shape.
mixin demoReply(expr)
    .bq-demo-bubble&attributes(attributes)
        img.bq-demo-bunny(src="/favicon-96x96.png" alt="" aria-hidden="true")
        p.bq-demo-reply-p
            | {{ #{expr}.reply.before }}
            strong {{ #{expr}.reply.strong }}
            | {{ #{expr}.reply.mid }}
            span(translate="no") {{ #{expr}.folder }}
            | :
        table.bq-demo-table(translate="no")
            thead
                tr
                    th(v-for=`h in ${expr}.table.head` :key="h") {{ h }}
            tbody
                tr(v-for=`(r, ri) in ${expr}.table.rows` :key="ri")
                    td(v-for="(c, ci) in r" :key="ci") {{ c }}
        p.bq-demo-sources
            | Sources:&nbsp;
            span(translate="no") {{ #{expr}.sources }}
        span.bq-demo-file(translate="no") {{ #{expr}.file }} &darr;
//- Bare decorative glyph used by the step / reason / developer cards. No box,
//- border, or shadow: a framed tile read as a button, and these are not
//- clickable. Feather-style 24x24 strokes; .bq-icon-accent strokes are pink.
mixin iconTile
    span.bq-icon(aria-hidden="true")
        svg(width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
            block
.bq-landing
    main
        section#what.bq-hero
            .bq-hero-landing-bunny
                BunnyLoader(:overlay="false")
            h1 BunnyQuery
            h3 AI Indexed Data Storage
            br
            p.bq-hero-body Talk to your data instead of digging through dashboards, forms, #[span.wordset folders, and files.]#[br]#[br]#[span.wordset Upload your documents,] spreadsheets, images, notes, and business records. #[span.wordset BunnyQuery turns] them into usable database, so your AI can answer questions, #[span.wordset generate reports,] and summarize information.
            p Turn your data into smarter decisions with BunnyQuery.
            .bq-hero-tags
                span.bq-hero-tag 50+ file formats
                span.bq-hero-tag OCR for images &amp; PDFs
                span.bq-hero-tag CSV report export
                span.bq-hero-tag MCP compatible
            br
            .bq-hero-body
                router-link.bq-btn(
                    data-track="landing_bq_hero_signup",
                    :to="user?.user_id ? '/my-projects' : '/signup'"
                ) Try BunnyQuery &rarr;
            //- Static mockup of the product's chat: upload + indexing -> indexed -> ask ->
            //- answer with a table -> exportable CSV. Illustration only, no JS.
            .bq-demo
                .bq-demo-bar
                    img.bq-demo-bar-icon(src="/favicon-96x96.png" alt="" aria-hidden="true")
                    span.bq-demo-bar-title(translate="no") BunnyQuery &middot; {{ cur.project }}
                    svg.bq-demo-bar-gear(width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true")
                        circle(cx="12" cy="12" r="3")
                        path(d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z")
                //- The conversation assembles itself: runDemo() (script) walks
                //- demo.step through index -> type -> send -> reply
                //- and loops; prefers-reduced-motion gets the finished state.
                //- The stage holds TWO copies of the chat: the finished one,
                //- invisible, fixes the window's height for the whole loop; the
                //- live gated one sits absolutely over it, composer pinned to
                //- the bottom like the real widget.
                .bq-demo-stage
                    //- One finished copy of EVERY scenario, stacked in a single
                    //- grid cell: the tallest fixes the window's height, so the
                    //- window never resizes when the loop rotates scenarios.
                    .bq-demo-chat--sizer(aria-hidden="true")
                        .bq-demo-chat.bq-demo-sizer-copy(v-for="s in DEMO_SCENARIOS" :key="s.project")
                            .bq-demo-msgs
                                .bq-demo-note
                                    span.material-symbols-outlined.bq-demo-folder-icon(translate="no" aria-hidden="true") folder
                                    span.bq-demo-note-file(translate="no") {{ s.folder }}
                                    span.bq-demo-note-state &middot; {{ s.indexedLabel }} &#10003;
                                .bq-demo-msg.bq-demo-msg--user
                                    .bq-demo-bubble {{ s.question }}
                                .bq-demo-msg
                                    +demoReply('s')
                            .bq-demo-composer
                                .bq-demo-input
                                    svg.bq-demo-attach(width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true")
                                        path(d="M21.44 11.05l-9.19 9.19a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 1 1 5.66 5.66l-9.2 9.19a2 2 0 1 1-2.83-2.83l8.49-8.48")
                                    span.bq-demo-placeholder Ask anything about: #[span(translate="no") {{ s.project }}]
                                span.bq-demo-send.bq-demo-send--disabled Send
                    .bq-demo-chat.bq-demo-chat--live
                        .bq-demo-msgs
                            .bq-demo-note(v-if="demo.step >= DS.indexing" :class="{ 'is-working': demo.step < DS.indexed }")
                                span.material-symbols-outlined.bq-demo-folder-icon(translate="no" aria-hidden="true") folder
                                span.bq-demo-note-file(translate="no") {{ cur.folder }}
                                template(v-if="demo.step < DS.indexed")
                                    span.bq-demo-note-state &middot; indexing
                                    span.bq-demo-dots(aria-hidden="true")
                                template(v-else)
                                    span.bq-demo-note-state &middot; {{ cur.indexedLabel }} &#10003;
                            //- While the demo user "types", a user-side bubble runs
                            //- the dot-trail: the same drafting indicator the real
                            //- clients show. The sent bubble replaces it in place.
                            .bq-demo-msg.bq-demo-msg--user(v-if="demo.step === DS.typing")
                                .bq-demo-bubble
                                    span.bq-demo-dots(aria-hidden="true")
                            .bq-demo-msg.bq-demo-msg--user(v-if="demo.step >= DS.sent")
                                .bq-demo-bubble {{ cur.question }}
                            .bq-demo-msg(v-if="demo.step >= DS.thinking")
                                //- Thinking... then the reply swaps in.
                                .bq-demo-bubble.bq-demo-bubble--thinking(v-if="demo.step < DS.replied")
                                    img.bq-demo-bunny(src="/favicon-96x96.png" alt="" aria-hidden="true")
                                    span.bq-demo-dots(aria-hidden="true")
                                +demoReply('cur')(v-else)
                        //- Upload chip with the real widget's progress fill (::before
                        //- width follows --att-progress). Shown WHILE the yellow
                        //- indexing row works, so upload and indexing overlap; both
                        //- resolve together into the green "indexed" row.
                        span.bq-demo-chip(v-if="demo.step === DS.indexing" translate="no" :style="{ '--att-progress': demo.pct + '%' }")
                            span.material-symbols-outlined.bq-demo-folder-icon(aria-hidden="true") folder
                            span.bq-demo-chip-name {{ cur.folder }}
                            span.bq-demo-chip-pct {{ demo.pct }}%
                        //- Mirrors the real composer: the paperclip is the same SVG as
                        //- agent.vue's attach button, and Send is the app's .btn look.
                        .bq-demo-composer
                            .bq-demo-input
                                svg.bq-demo-attach(width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true")
                                    path(d="M21.44 11.05l-9.19 9.19a6 6 0 1 1-8.49-8.49l9.19-9.19a4 4 0 1 1 5.66 5.66l-9.2 9.19a2 2 0 1 1-2.83-2.83l8.49-8.48")
                                template(v-if="demo.typed")
                                    span.bq-demo-typed(translate="no") {{ demo.typed }}
                                    span.bq-demo-caret(aria-hidden="true")
                                span.bq-demo-placeholder(v-else) Ask anything about: #[span(translate="no") {{ cur.project }}]
                            span.bq-demo-send(:class="{ 'bq-demo-send--disabled': !demo.typed, 'is-pressed': demo.sendPressed }") Send
            p.bq-demo-caption Answers, reports, and exports come from #[span.wordset your own files,] not the open internet.

        section#how.bq-section.bq-section--alt
            .bq-section-header
                span.bq-pill HOW IT WORKS
                h2 AI Assistant for #[span.wordset Your Business Data]
                p.bq-section-sub Upload your files, then ask questions in natural language and get answers, reports, and insights back. #[span.wordset Setup takes three steps.]
            .bq-infra-grid
                .bq-step
                    +iconTile
                        rect(x="3" y="3" width="18" height="18")
                        path.bq-icon-accent(d="M12 8v8M8 12h8")
                    h3 1. Create a project
                    p Name your project and add a base prompt: a short note on what your data is and how you want the agent to handle it.
                .bq-step
                    +iconTile
                        path(d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4")
                    h3 2. Add your AI API key
                    p Choose ChatGPT or Claude, whichever model you prefer, and enter that platform's API key. Your own key powers your assistant.
                .bq-step
                    +iconTile
                        path(d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z")
                        path.bq-icon-accent(d="M12 15v-5M9.5 12L12 9.5 14.5 12")
                    h3 3. Start #[span.wordset BunnyQuery-ing!]
                    p Upload files or whole folders. Even huge datasets are indexed for you. Then ask questions, request analysis, and export reports.

        section#why.bq-section.bq-hero
            .bq-section-header.bq-section-header--split
                div
                    span.bq-pill WHY BUNNYQUERY
                    h2 Your data. #[span.wordset Your AI.] #[span.wordset Your rules.]
                    p.bq-section-sub Use AI without giving up control of your data.
                img.bq-why-bunny(src="/web-app-manifest-512x512.png" alt="BunnyQuery bunny inspecting data with a magnifying glass")
            .bq-reasons
                .bq-reason
                    +iconTile
                        path(d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z")
                        circle.bq-icon-accent(cx="12" cy="10" r="1.8")
                        path.bq-icon-accent(d="M12 11.8v3.2")
                    .bq-reason-body
                        span.bq-reason-title Safe from AI platforms
                        p BunnyQuery prevents AI platforms from using your data to train their AI models or exposing your data.
                .bq-reason
                    //- Database cylinder with a check at the corner: answers
                    //- come from a lookup you can verify, not from a model's memory.
                    +iconTile
                        ellipse(cx="11" cy="5" rx="8" ry="3")
                        path(d="M3 5v14c0 1.66 3.58 3 8 3 .7 0 1.37-.03 2-.1")
                        path(d="M3 12c0 1.66 3.58 3 8 3 1.4 0 2.7-.14 3.8-.4")
                        path(d="M19 5v6")
                        path.bq-icon-accent(d="M14.5 18.5l2.5 2.5 5-5")
                    .bq-reason-body
                        span.bq-reason-title Your source of truth
                        p Your AI never answers from memory. Nothing is trained on your data: every answer comes from a lookup in your own database, and every record traces back to the file, sheet, and row it came from. Wrong answers are traceable and corrections are a single edit, not a retrain.

                .bq-reason
                    +iconTile
                        path(d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z")
                        path(d="M14 2v6h6")
                        circle.bq-icon-accent(cx="10.5" cy="13.5" r="2.8")
                        path.bq-icon-accent(d="M12.7 15.7L15 18")
                    .bq-reason-body
                        span.bq-reason-title Reads basically everything, Supported formats include:
                        p #[span(translate="no") .doc, .docx, .docm, .xls, .xlsx, .xlsm, .ppt, .pptx, .pptm, .hwp, .hwpx, .ods, .odt, .odp, .epub, .eml, .pdf, .jpg, .jpeg, .png, .gif, .webp, .html, .htm, .xml, .svg, .csv, .tsv, .tab, .txt, .text, .log, .md, .markdown, .rst] ...and a lot more.
                            //- | .json, .ndjson, .jsonl, .geojson, .yaml, .yml, .toml, .ini, .conf, .cfg, .properties, .env, .rtf, .srt, .vtt, .tex, .js, .mjs, .cjs, .ts, .tsx, .jsx, .py, .rb, .go, .rs, .java, .kt, .c, .h, .cpp, .cc, .hpp, .cs, .php, .swift, .sh, .bash, .zsh, .sql, .css, .scss, .less, .vue, .svelte
                .bq-reason
                    +iconTile
                        path(d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2")
                        circle(cx="9" cy="7" r="4")
                        path.bq-icon-accent(d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75")
                    .bq-reason-body
                        span.bq-reason-title Built for collaboration
                        p Invite teammates, clients, or customers to ask questions directly. They get answers, reports, and the information they need without waiting on you.

        section#dev.bq-section.bq-section--alt
            .bq-section-header
                span.bq-pill FOR DEVELOPERS
                br
                SkapiBunnyLockup
                h2 Expandable with Developer Options
                p.bq-section-sub BunnyQuery is built on Skapi, a serverless platform that scales with zero downtime. #[span.wordset Your project] is compatible with Skapi backend: connect it over MCP, embed the chat widget, or build your own web service on top.
            //- The developer story in one picture: files become an indexed
            //- context database, your AI answers from it, and the same database
            //- is open to any MCP client or your own app. Fixed-width drawing in
            //- a scrollable frame so phones pan it instead of shrinking the text.
            //- .bq-flow(role="img" aria-label="Diagram: your files are indexed into a context database; ChatGPT or Claude answers from it; the same database is reachable from any MCP client or your own web service.")
            //-     svg.bq-flow-svg(viewBox="0 0 880 228" width="880" height="228" aria-hidden="true")
            //-         defs
            //-             marker#bq-flow-arrow(viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse")
            //-                 path.bq-flow-head(d="M0 0L10 5L0 10z")
            //-             marker#bq-flow-arrow-pink(viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse")
            //-                 path.bq-flow-head--pink(d="M0 0L10 5L0 10z")
            //-         //- main pipeline
            //-         rect.bq-flow-shadow(x="15" y="27" width="150" height="64")
            //-         rect.bq-flow-box(x="12" y="24" width="150" height="64")
            //-         text.bq-flow-t(x="87" y="51" text-anchor="middle") your files
            //-         text.bq-flow-s(x="87" y="71" text-anchor="middle") docs · sheets · images
            //-         line.bq-flow-line(x1="168" y1="56" x2="212" y2="56" marker-end="url(#bq-flow-arrow)")
            //-         rect.bq-flow-shadow(x="225" y="27" width="195" height="64")
            //-         rect.bq-flow-box(x="222" y="24" width="195" height="64")
            //-         text.bq-flow-t(x="319" y="51" text-anchor="middle") context database
            //-         text.bq-flow-s(x="319" y="71" text-anchor="middle") indexed, queryable records
            //-         line.bq-flow-line(x1="423" y1="56" x2="463" y2="56" marker-end="url(#bq-flow-arrow)")
            //-         rect.bq-flow-shadow(x="476" y="27" width="160" height="64")
            //-         rect.bq-flow-box(x="473" y="24" width="160" height="64")
            //-         text.bq-flow-t(x="553" y="51" text-anchor="middle") your AI
            //-         text.bq-flow-s(x="553" y="71" text-anchor="middle") ChatGPT · Claude
            //-         line.bq-flow-line(x1="639" y1="56" x2="679" y2="56" marker-end="url(#bq-flow-arrow)")
            //-         rect.bq-flow-shadow(x="692" y="27" width="179" height="64")
            //-         rect.bq-flow-box(x="689" y="24" width="179" height="64")
            //-         text.bq-flow-t(x="778" y="51" text-anchor="middle") answers
            //-         text.bq-flow-s(x="778" y="71" text-anchor="middle") reports · CSV exports
            //-         //- the branch: the same database, open to the outside
            //-         line.bq-flow-line--pink(x1="319" y1="94" x2="319" y2="146" marker-end="url(#bq-flow-arrow-pink)")
            //-         text.bq-flow-b(x="330" y="124" text-anchor="start") MCP · Skapi SDK
            //-         rect.bq-flow-shadow(x="187" y="157" width="270" height="60")
            //-         rect.bq-flow-box(x="184" y="154" width="270" height="60")
            //-         text.bq-flow-t(x="319" y="179" text-anchor="middle") your own integrations
            //-         text.bq-flow-s(x="319" y="199" text-anchor="middle") any MCP client · your web service
            .bq-infra-grid
                //- Whole card is the anchor (like the news cards); the url line
                //- is a span since anchors can't nest.
                a.bq-infra-card(href="https://mcp.broadwayinc.computer" target="_blank" rel="noopener")
                    +iconTile
                        circle(cx="18" cy="5" r="3")
                        circle.bq-icon-accent(cx="6" cy="12" r="3")
                        circle(cx="18" cy="19" r="3")
                        path(d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98")
                    h3 MCP Connectivity
                    p Skapi MCP connects your project's context database to any AI platform that supports MCP.
                    p.bq-section-link
                        span(translate="no") mcp.broadwayinc.computer &rarr;
                a.bq-infra-card(href="https://www.skapi.com" target="_blank" rel="noopener")
                    +iconTile
                        path(d="M16 18l6-6-6-6")
                        path.bq-icon-accent(d="M8 6l-6 6 6 6")
                    h3 Develop with Skapi
                    p Connect your context database with Skapi and build your own web services.
                    p.bq-section-link
                        span(translate="no") www.skapi.com &rarr;
                a.bq-infra-card(href="https://github.com/broadwayinc/bunnyquery" target="_blank" rel="noopener")
                    +iconTile
                        rect(x="2" y="3" width="20" height="17")
                        path(d="M2 8h20")
                        path.bq-icon-accent(d="M8 12h8v4h-5l-2 2v-2H8z")
                    h3 HTML Embeddable Chat Widget
                    p Embed a full chat UI with one script tag, or import the engine to build your own.
                    p.bq-section-link
                        span BunnyQuery Widget &rarr;
        //- Latest articles. Hidden until the static index loads; hidden for
        //- good if it fails (a marketing page shows no error states).
        section#news.bq-section(v-if="newsArticles.length")
            .bq-section-header
                span.bq-pill NEWS &amp; UPDATES
                h2 Latest from BunnyQuery
                p.bq-section-sub Release notes, announcements, and stories from the team.
            .bq-news-grid
                router-link.bq-news-card(
                    v-for="a in newsArticles"
                    :key="a.slug"
                    :to="`/articles/${a.slug}`"
                    :data-track="`landing_article_${a.slug}`"
                )
                    time.bq-news-date(translate="no") {{ a.date }}
                    h3.bq-news-title {{ a.title }}
                    p.bq-news-excerpt(v-if="a.excerpt") {{ a.excerpt }}
                    span.bq-news-more(aria-hidden="true") Read &rarr;
            p.bq-section-link
                router-link.bq-cta-link(to="/articles" data-track="landing_articles_all")
                    | All articles
                    span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        section.bq-section--alt
            .bq-footer-cta
                .bq-landing-bunny
                    BunnyLoader(:overlay="false")
                h2 Try BunnyQuery #[span.wordset for Free]
                p Free to start. #[span.wordset No credit card required.]
                
                router-link.bq-btn(
                    data-track="landing_bq_bottom_services",
                    :to="user?.user_id ? '/my-projects' : '/signup'"
                ) Try BunnyQuery &rarr;
</template>

<script setup>
import { user } from "@/code/user";
import BunnyLoader from "@/components/bunnyLoader.vue";
import SkapiBunnyLockup from "@/components/skapiBunnyLockup.vue";
import { removeFullScreenLoader } from "@/components/jsComponents";
import { getArticles } from "@/code/articles";
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";

// Latest articles for the news section (newest first; section hides when
// empty or when the index fails to load).
const newsArticles = ref([]);

/* ---- hero chat-demo animation -------------------------------------------
 * A scripted loop through the product's happy path: index ->
 * type the question -> send -> thinking -> reply. Pure presentation state; the
 * template gates each piece on demo.step so the conversation assembles itself.
 * Each pass through the loop plays the next scenario, one per use-case card
 * (finance / business knowledge / personal health / research), so the one chat
 * window demos all four. A reply is before + strong + mid + folder + ":".
 */
const DS = { idle: 0, indexing: 1, indexed: 2, typing: 3, sent: 4, thinking: 5, replied: 6 };
const DEMO_SCENARIOS = [
    {
        // 001 Finance Assistant
        project: "business-finance",
        folder: "receipts_invoice",
        indexedLabel: "3,812 files indexed",
        question: "Break down my income and list my revenue sources.",
        reply: {
            before: "Income is up ",
            strong: "+11%",
            mid: " this quarter. Here is the monthly breakdown, compiled from the receipts and statements in ",
        },
        table: {
            head: ["Month", "Income", "Top source"],
            rows: [
                ["Apr", "$86k", "Wholesale"],
                ["May", "$94k", "Online store"],
                ["Jun", "$103k", "Online store"],
            ],
        },
        sources: "receipts/ (1,204 files) · bank-statements.pdf",
        file: "income-by-month.csv",
    },
    {
        // 002 Business Knowledge AI
        project: "team-ops",
        folder: "vendor_docs",
        indexedLabel: "5,309 files indexed",
        question: "Who is our biggest supplier, and who are their contacts?",
        reply: {
            before: "Your biggest supplier is ",
            strong: "Hanil Metals",
            mid: ": $1.2M across 87 purchase orders this year. Their contacts, pulled from the orders and vendor list in ",
        },
        table: {
            head: ["Name", "Role", "Last contact"],
            rows: [
                ["J. Park", "Account manager", "Jul 28"],
                ["S. Lee", "Billing", "Jul 12"],
                ["M. Choi", "Logistics", "Jun 30"],
            ],
        },
        sources: "purchase-orders/ (87 files) · vendor-list.xlsx",
        file: "supplier-contacts.csv",
    },
    {
        // 003 Personal Health Assistant
        project: "my-health",
        folder: "medical_results",
        indexedLabel: "214 files indexed",
        question: "Show how my blood sugar has changed over time.",
        reply: {
            before: "Your fasting glucose is trending down, ",
            strong: "-14 mg/dL",
            mid: " since March. Compiled from the lab reports in ",
        },
        table: {
            head: ["Date", "Fasting", "HbA1c"],
            rows: [
                ["Mar 04", "118 mg/dL", "6.1%"],
                ["May 12", "109 mg/dL", "5.8%"],
                ["Jul 21", "104 mg/dL", "5.6%"],
            ],
        },
        sources: "lab-reports/ (9 files) · prescriptions/ (32 files)",
        file: "glucose-trend.csv",
    },
    {
        // 004 Research and Document Analysis
        project: "research-lab",
        folder: "research_papers",
        indexedLabel: "12 files indexed",
        question: "Summarize the key findings from these research papers.",
        reply: {
            before: "The 12 papers converge on ",
            strong: "3 key findings",
            mid: ", each cited back to its source in ",
        },
        table: {
            head: ["#", "Finding", "Papers"],
            rows: [
                ["1", "Solid electrolytes double cycle life", "7 of 12"],
                ["2", "Degradation starts at the cathode", "5 of 12"],
                ["3", "Fast charging trades 9% capacity", "4 of 12"],
            ],
        },
        sources: "research_papers/ (12 files, 2019-2025)",
        file: "key-findings.csv",
    },
];
const demo = reactive({ step: DS.idle, pct: 0, typed: "", sendPressed: false, scenario: 0 });
const cur = computed(() => DEMO_SCENARIOS[demo.scenario]);

let demoTimers = [];
// One ticker handle for the upload progress bar, then the typewriter. In a
// foreground tab the phases never overlap, but a throttled background tab can
// stretch one into the next, so every taker clears the handle before
// reassigning it (an overwritten interval would leak forever).
let demoTicker = null;
const clearDemoTicker = () => {
    if (demoTicker) { clearInterval(demoTicker); demoTicker = null; }
};
const demoLater = (fn, ms) => demoTimers.push(setTimeout(fn, ms));

const runDemo = () => {
    // Everything scheduled by the previous cycle has fired by the time the
    // loop re-enters (runDemo IS the last timer), so drop the spent handles.
    demoTimers = [];
    // A throttled background tab can leave the previous phase's interval
    // running into this one; clear it before anything reassigns the handle.
    clearDemoTicker();
    demo.step = DS.idle;
    demo.pct = 0;
    demo.typed = "";
    demo.sendPressed = false;
    // Upload and indexing run TOGETHER: the yellow note and the progress chip
    // appear as one, and both resolve into the green "indexed" row.
    demoLater(() => {
        demo.step = DS.indexing;
        demoTicker = setInterval(() => {
            demo.pct = Math.min(100, demo.pct + 9);
            if (demo.pct >= 100 && demoTicker) { clearInterval(demoTicker); demoTicker = null; }
        }, 100);
    }, 400);
    demoLater(() => { demo.step = DS.indexed; }, 2000);
    const question = DEMO_SCENARIOS[demo.scenario].question;
    demoLater(() => {
        demo.step = DS.typing;
        // The progress ticker may still be running in a throttled tab; take
        // the handle over cleanly instead of orphaning the old interval.
        clearDemoTicker();
        let i = 0;
        demoTicker = setInterval(() => {
            i += 1;
            demo.typed = question.slice(0, i);
            if (i >= question.length && demoTicker) { clearInterval(demoTicker); demoTicker = null; }
        }, 36);
    }, 2500);
    // Questions differ in length per scenario, so everything after the
    // typewriter is scheduled off its real duration, not a fixed offset.
    const sendAt = 2500 + question.length * 36 + 400;
    demoLater(() => { demo.sendPressed = true; }, sendAt);
    // Clear the typewriter here too: throttled, it could outlive the send and
    // repaint stale composer text into the thinking/replied phases.
    demoLater(() => { demo.sendPressed = false; clearDemoTicker(); demo.typed = ""; demo.step = DS.sent; }, sendAt + 200);
    demoLater(() => { demo.step = DS.thinking; }, sendAt + 600);
    demoLater(() => { demo.step = DS.replied; }, sendAt + 2100);
    // Hold the finished conversation for 3s, then play the next scenario.
    demoLater(() => {
        demo.scenario = (demo.scenario + 1) % DEMO_SCENARIOS.length;
        runDemo();
    }, sendAt + 5100);
};

onMounted(() => {
    removeFullScreenLoader();
    getArticles()
        .then((list) => { newsArticles.value = list.slice(0, 3); })
        .catch(() => { /* section stays hidden */ });
    // Motion-sensitive visitors get the finished conversation, statically.
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        demo.step = DS.replied;
        return;
    }
    runDemo();
});

onUnmounted(() => {
    demoTimers.forEach(clearTimeout);
    demoTimers = [];
    clearDemoTicker();
});
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-landing {
    min-height: 100vh;
    margin: 0;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 16px;
    line-height: 1.7;
    overflow-x: hidden;

    button {
        all: unset;
        box-sizing: border-box;
        cursor: pointer;

        &::after {
            display: none;
        }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        font-weight: inherit;
    }

    hr {
        background: @line;
    }
}

/* ─── hero ─── */
.bq-hero {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem 2rem 5rem;
    border-bottom: 1px solid @line;

    h1 {
        margin: 0.75rem 0 1.5rem;
        font-size: clamp(2.6rem, 5vw, 4.4rem);
        line-height: 1.08;
        font-weight: 700;
        text-shadow: var(--bq-text-shadow);
    }
}

.bq-hero-body {
    margin: 0 0 2rem;
    color: @muted;
    max-width: 800px;
}
.bq-hero-landing-bunny {
    margin: 2.5rem 0 2rem;
    width: 100%;
    position: relative;

    :deep(.bq-bunny-loader) {
        align-items: center;
    }

    :deep(.bq-bunny-stage) {
        width: 100%;
        overflow: hidden;
    }

    :deep(.bq-bunny-track) {
        height: 8rem;
        animation-name: bq-hero-bunny-move;
    }

    // The frame is anchored left:0 inside the full-width track, so the hop
    // started from the left edge. Re-anchor it to the middle (margin, not
    // transform — the flip keyframes own `transform` on this element) so the
    // symmetric keyframes swing around a CENTERED rest position.
    :deep(.bq-bunny-dir) {
        left: 50%;
        margin-left: -4ch;
    }
}

/* Symmetric hop around the centered rest position, same 22ch travel. */
@keyframes bq-hero-bunny-move {
    0%     { transform: translateX(-11ch); }
    25%    { transform: translateX(11ch); }
    37.5%  { transform: translateX(11ch); }
    50%    { transform: translateX(11ch); }
    75%    { transform: translateX(-11ch); }
    87.5%  { transform: translateX(-11ch); }
    100%   { transform: translateX(-11ch); }
}

/* The hero (#what only — .bq-hero is reused by #why) is centered: the
   bunny above, every text block below it, the tag row, and the demo window,
   which stays left-aligned INSIDE. */
#what.bq-hero {
    text-align: center;

    .bq-hero-body {
        margin-left: auto;
        margin-right: auto;
    }

    .bq-hero-tags {
        justify-content: center;
    }

    .bq-demo {
        margin-left: auto;
        margin-right: auto;
        text-align: left;
    }
}

/* ─── button ─── */
.bq-btn {
    display: inline-block;
    padding: 0.85rem 1.6rem;
    border: 1px solid @ink;
    background: @ink;
    color: @paper;
    font: inherit;
    font-size: 0.9rem;
    text-decoration: none;
    cursor: pointer;
    /* hover extrude + press-down come from the global a.bq-btn rules in
       base.less, shared with every block button */
    transition: transform 0.12s ease, box-shadow 0.12s ease;
    box-shadow: 2px 2px var(--bq-shadow);
}

/* ─── sections ─── */
.bq-section {
    max-width: 1200px;
    margin: 0 auto;
    padding: 5rem 2rem;
}

.bq-section--alt {
    background: var(--bq-alt-bg);
    max-width: 100%;
    padding-left: 0;
    padding-right: 0;

    .bq-section-header,
    .bq-reasons,
    .bq-flow,
    .bq-infra-grid {
        max-width: 1200px;
        margin-left: auto;
        margin-right: auto;
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.bq-section-header {
    margin-bottom: 3rem;

    h2 {
        margin: 0 0 0.75rem;
        font-size: clamp(1.6rem, 3.5vw, 2.6rem);
        line-height: 1.15;
        font-weight: 600;
        // max-width: 44rem;
        text-shadow: var(--bq-text-shadow);
    }
}

.bq-section-sub {
    margin: 0;
    color: @muted;
    max-width: 800px;
}

// Layout only. The "All articles →" link inside it is a plain text link and
// follows the site rule through .bq-cta-link: ink at rest, underlined, pink on
// hover, arrow undecorated. A scoped `a { color: @pink }` used to live here and,
// at (0,2,1), outranked the shared class -- so the link sat pink at rest and had
// nowhere to go on hover. The url lines inside the infra cards are spans, not
// anchors, and keep their own pink via .bq-infra-card .bq-section-link span.
.bq-section-link {
    margin: 1rem 0 0;
    font-size: 0.9rem;
}

/* ─── steps ─── */
.bq-step {
    padding: 1.25rem 1.5rem 1.4rem;
    border: 1px solid @line;
    background: @paper;

    /* the glyph replaces the old step number at the top of the card */
    .bq-icon {
        margin-bottom: 0.65rem;
    }

    h3 {
        margin: 0 0 0.4rem;
        font-size: 1.05rem;
    }

    p {
        margin: 0;
        color: @muted;
        font-size: 0.92rem;
        line-height: 1.55;
    }
}

/* ─── feature icons: bare decorative glyphs ─── */
.bq-icon {
    display: inline-flex;
    flex-shrink: 0;
    color: @ink;

    svg {
        display: block;
    }

    .bq-icon-accent {
        stroke: @pink;
    }
}

/* ─── reasons ─── */
.bq-reasons {
    display: grid;
    gap: 0;
}

.bq-reason {
    display: flex;
    gap: 1.25rem;
    /* flex-start, not baseline: the icon has no useful text baseline. The
       icon and num instead carry tiny offsets that center each of them on
       the title's FIRST LINE box (1.05rem font at the page's 1.7 line
       height = 1.785rem tall). */
    align-items: flex-start;
    border-bottom: 1px solid @line;
    padding: 1.5rem 0;

    &:last-child {
        border-bottom: none;
    }

    p {
        margin: 0.35rem 0 0;
        color: @muted;
        font-size: 0.95rem;
    }

    /* (1.785rem line - 24px glyph) / 2 */
    .bq-icon {
        margin-top: 0.14rem;
    }
}

.bq-reason-body {
    flex: 1;
    min-width: 0;
}

.bq-reason-title {
    display: block;
    font-weight: 600;
    font-size: 1.05rem;
}

/* ─── infra ─── */
.bq-infra-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
}

/* ─── developer flow diagram ─── */
/* The drawing keeps its natural width; narrow screens pan it horizontally
   instead of shrinking the labels into illegibility. */
.bq-flow {
    margin-bottom: 2.5rem;
    overflow-x: auto;
}

.bq-flow-svg {
    display: block;
}

.bq-flow-box {
    fill: @paper;
    stroke: @ink;
}

.bq-flow-shadow {
    fill: var(--bq-shadow);
}

.bq-flow-t {
    fill: @ink;
    font-family: @mono;
    font-size: 14px;
    font-weight: 600;
}

.bq-flow-s {
    fill: @muted;
    font-family: @mono;
    font-size: 12px;
}

.bq-flow-b {
    fill: @pink;
    font-family: @mono;
    font-size: 12px;
    font-weight: 600;
}

.bq-flow-line {
    stroke: @ink;
    stroke-width: 1.5;
}

.bq-flow-line--pink {
    stroke: @pink;
    stroke-width: 1.5;
}

.bq-flow-head {
    fill: @ink;
}

.bq-flow-head--pink {
    fill: @pink;
}

.bq-infra-card {
    /* the card itself is an anchor now */
    display: block;
    color: @ink;

    .bq-icon {
        margin-bottom: 0.75rem;
    }
    text-decoration: none;
    padding: 1.5rem;
    border: 1px solid @line;
    /* paper, not the section's alt tint: the cards sit on the alt section
       and should pop off it like the step and news cards do */
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    /* same extruding hover as the news cards */
    transition: transform 0.12s ease, box-shadow 0.12s ease;

    &:hover {
        text-decoration: none;
        transform: translate(-1px, -1px);
        box-shadow: 4px 4px var(--bq-shadow);
    }

    h3 {
        margin: 0 0 0.5rem;
        font-size: 1.05rem;
    }

    p {
        margin: 0;
        color: @muted;
        font-size: 0.95rem;
    }

    /* the url line inside the card is a span (anchors can't nest) — plain
       colored text like the news cards' "Read →", no hover underline */
    .bq-section-link span {
        color: @pink;
    }
}

/* ─── hero tags ─── */
.bq-hero-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1.25rem 0 0;
}

.bq-hero-tag {
    padding: 0.25rem 0.6rem;
    border: 1px solid @line;
    background: @paper;
    color: @muted;
    font-size: 0.78rem;
    white-space: nowrap;
}

/* ─── hero chat demo ─── */
.bq-demo {
    max-width: 780px;
    margin: 3rem 0 0;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 4px 4px var(--bq-shadow);
    font-size: 0.88rem;
}

.bq-demo-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.55rem 0.9rem;
    color: @muted;
    font-size: 0.8rem;
}

.bq-demo-bar-icon {
    width: 1.1rem;
    height: 1.1rem;
    object-fit: contain;
}

.bq-demo-bar-gear {
    margin-left: auto;
    flex-shrink: 0;
}

.bq-demo-chat {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    padding: 1.1rem;
}

/* The invisible finished conversations reserve the height; the live copy
   plays on top of them, so the window never resizes mid-loop. */
.bq-demo-stage {
    position: relative;
    overflow: hidden;
}

/* Every scenario's finished conversation overlaps in ONE grid cell, so the
   tallest of them sets the window's height for the whole rotation. */
.bq-demo-chat--sizer {
    visibility: hidden;
    pointer-events: none;
    display: grid;

    * {
        animation: none !important;
    }
}

.bq-demo-sizer-copy {
    grid-area: 1 / 1;
    min-width: 0;
}

.bq-demo-chat--live {
    position: absolute;
    inset: 0;
}

/* Messages grow from the top; chip + composer stay pinned to the bottom. */
.bq-demo-msgs {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
}

.bq-demo-note {
    align-self: stretch;
    padding: 0.45rem 0.8rem;
    border: 1px solid @success-border;
    background: @success-bg;
    font-size: 0.78rem;
}

.bq-demo-note-state {
    /* Pug emits no whitespace between sibling inline tags, so the gap after
       the filename has to be layout, not text. */
    margin-left: 0.3rem;
    color: @success;
}

.bq-demo-msg {
    display: flex;
    align-items: flex-end;
    gap: 0.6rem;
    max-width: 92%;
}

.bq-demo-msg--user {
    align-self: flex-end;
    max-width: 85%;

    .bq-demo-bubble {
        background: @pink-bg;
        /* flat like the real user bubbles: the shadow stays on replies only */
        box-shadow: none;
    }
}

.bq-demo-bubble {
    min-width: 0;
    border: 1px solid @line;
    background: @paper;
    padding: 0.65rem 0.9rem;
    line-height: 1.55;
    overflow-x: auto;
    box-shadow: 2px 2px var(--bq-shadow);

    /* same bunny-mark treatment as the real reply bubbles (chat.css) */
    .bq-demo-bunny {
        display: block;
        width: 2.6em;
        height: 2.6em;
        left: -4px;
        top: -4px;
        position: relative;
        object-fit: contain;
    }

    p {
        margin: 0 0 0.6rem;
    }

    p:last-child {
        margin: 0;
    }
}

.bq-demo-table {
    border-collapse: collapse;
    margin: 0 0 0.6rem;
    font-size: 0.8rem;

    th,
    td {
        border: 1px solid @line;
        padding: 0.3rem 0.6rem;
        text-align: left;
    }

    th {
        background: var(--bq-alt-bg);
        font-weight: 600;
    }
}

.bq-demo-file {
    display: inline-block;
    padding: 0.3rem 0.7rem;
    border: 1px solid @line;
    color: @pink;
    font-size: 0.8rem;
}

/* Mirrors the real .bq-input-row: bordered input with the clip in its left
   gutter, Send as the app's .btn (ink block, hard shadow) beside it. */
.bq-demo-composer {
    display: flex;
    align-items: stretch;
    gap: 0.65rem;
    margin-top: 0.2rem;
}

.bq-demo-input {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-width: 0;
    min-height: 2.6rem;
    padding: 0 0.7rem;
    border: 1px solid @line;
    background: @paper;
}

.bq-demo-attach {
    flex-shrink: 0;
    color: @muted;
}

.bq-demo-placeholder {
    flex: 1;
    color: @placeholder;
    font-size: 0.83rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bq-demo-send {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 5.5rem;
    padding: 0 1.4rem;
    border: 1px solid @ink;
    background: @ink;
    color: @paper;
    font-size: 0.88rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    box-shadow: 2px 2px var(--bq-shadow);
}

/* nothing typed yet, so Send reads disabled, same as the real .btn:disabled */
.bq-demo-send--disabled {
    opacity: 0.3;
}

/* ─── demo animation ─── */
/* While the file is still being read: the working (warning) tint, same
   colour language as the real collapsed indexing row. */
.bq-demo-note.is-working {
    border-color: @warning-border;
    background: @warning-bg;

    .bq-demo-note-state {
        color: @warning;
    }
}

/* Attachment chip during the overlapped upload+indexing beat, above the
   composer like the real attachments row, including its progress fill: a
   ::before whose width follows --att-progress, exactly the real widget's
   .bq-attachment. */
.bq-demo-chip {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.7rem;
    border: 1px solid @line;
    background: @paper;
    font-size: 0.78rem;

    &::before {
        content: "";
        position: absolute;
        inset: 0 auto 0 0;
        width: var(--att-progress, 0%);
        background: color-mix(in srgb, @pink 18%, transparent);
        transition: width 0.15s linear;
        pointer-events: none;
    }

    > * {
        position: relative;
        z-index: 1;
    }
}

.bq-demo-chip-pct {
    color: @muted;
}

/* The per-file citation line under the table. */
.bq-demo-sources {
    font-size: 0.72rem;
    color: @muted;
}

/* Filled folder glyph, like the real attachment chip's folder (is-filled). */
.bq-demo-folder-icon {
    font-size: 1.15em;
    font-variation-settings: "FILL" 1;
    vertical-align: -0.22em;
    margin-right: 0.25rem;
}

.bq-demo-typed {
    color: @ink;
    font-size: 0.83rem;
    white-space: nowrap;
    overflow: hidden;
}

.bq-demo-caret {
    flex-shrink: 0;
    width: 1px;
    height: 1.1em;
    margin-left: 1px;
    background: @ink;
    animation: bqd-blink 1s steps(2) infinite;
}

/* Thinking only: the bunny sits inline with the dot-trail, not above it
   (mirrors the real chat's pending-bubble rule in chat.css). */
.bq-demo-bubble--thinking .bq-demo-bunny {
    display: inline-block;
    vertical-align: middle;
    margin-right: 0.4rem;
}

.bq-demo-bubble--thinking .bq-demo-dots {
    vertical-align: middle;
}

/* The real widget's dot-trail: solid ".", "..", "..." stepped, no fading. */
.bq-demo-dots {
    color: @muted;
    letter-spacing: 0.2em;

    &::after {
        content: ".";
        animation: bqd-dots 1.2s steps(1) infinite;
    }
}

.bq-demo-note .bq-demo-dots {
    color: @warning;
}

/* The hard shadow presses flat for the beat the demo "clicks" Send. */
.bq-demo-send.is-pressed {
    transform: translate(2px, 2px);
    box-shadow: none;
}

@keyframes bqd-blink {
    50% {
        opacity: 0;
    }
}

@keyframes bqd-dots {
    0% {
        content: ".";
    }

    33% {
        content: "..";
    }

    66% {
        content: "...";
    }
}

@media (prefers-reduced-motion: no-preference) {
    /* Each piece slides in as the script inserts it. */
    .bq-demo-note,
    .bq-demo-chip,
    .bq-demo-msg,
    .bq-demo-bubble {
        animation: bqd-in 0.28s ease;
    }

    /* The reply reveals in reading order: text, table, sources, the file. */
    .bq-demo-reply-p {
        animation: bqd-in 0.3s ease both;
    }

    .bq-demo-bubble .bq-demo-table {
        animation: bqd-in 0.35s ease 0.25s both;
    }

    .bq-demo-sources {
        animation: bqd-in 0.3s ease 0.4s both;
    }

    .bq-demo-file {
        animation: bqd-in 0.3s ease 0.55s both;
    }
}

@keyframes bqd-in {
    from {
        opacity: 0;
        transform: translateY(6px);
    }

    to {
        opacity: 1;
        transform: none;
    }
}

.bq-demo-caption {
    margin: 0.9rem 0 0;
    color: @muted;
    font-size: 0.9rem;
}

/* ─── why bunny ─── */
.bq-section-header--split {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
}

.bq-why-bunny {
    width: 9.4rem;
    height: 9.4rem;
    object-fit: contain;
    flex-shrink: 0;
    padding: 0.75rem;
    left: -15px;
    position: relative;
    // border: 1px solid @line;
    // background: @paper;
    // box-shadow: 2px 2px var(--bq-shadow);
}

/* ─── news / articles ─── */
.bq-news-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.bq-news-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.5rem;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    color: @ink;
    text-decoration: none;
    transition: transform 0.12s ease, box-shadow 0.12s ease;

    &:hover {
        text-decoration: none;
        transform: translate(-1px, -1px);
        box-shadow: 4px 4px var(--bq-shadow);
    }
}

.bq-news-date {
    font-size: 0.78rem;
    color: @muted;
}

.bq-news-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
}

.bq-news-excerpt {
    flex: 1;
    margin: 0;
    color: @muted;
    font-size: 0.92rem;
    line-height: 1.55;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.bq-news-more {
    color: @pink;
    font-size: 0.85rem;
}

/* ─── plans ─── */

/* ─── footer cta ─── */
.bq-footer-cta {
    max-width: 1200px;
    margin: 0 auto;
    // padding: 5rem 1.5rem;
    padding: 8rem 1.5rem 18rem;
    text-align: center;

    h2 {
        margin: 0 0 0.75rem;
        font-size: clamp(1.8rem, 4vw, 2.8rem);
        font-weight: 700;
        text-shadow: var(--bq-text-shadow);
    }

    p {
        margin: 0 0 2rem;
        color: @muted;
    }
}

/* ─── bunny animation ─── */
.bq-landing-bunny {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1rem;
}

/* ─── responsive ─── */
@media (max-width: 900px) {
    .bq-hero {
        padding: 1rem 1.5rem 3rem;
    }

    .bq-hero .bq-btn,
    .bq-footer-cta .bq-btn {
        display: block;
        width: 100%;
        text-align: center;
    }

    .bq-infra-grid {
        grid-template-columns: 1fr;
    }

    .bq-news-grid {
        grid-template-columns: 1fr;
    }

    .bq-section {
        padding: 3rem 1.5rem;
    }

    .bq-section--alt {
        padding-left: 0;
        padding-right: 0;

        .bq-section-header,
        .bq-reasons,
        .bq-flow,
        .bq-infra-grid {
            padding-left: 1.5rem;
            padding-right: 1.5rem;
        }
    }

    .bq-section-header {
        margin-bottom: 2rem;
    }

    .bq-demo {
        margin-top: 2.5rem;
    }

    .bq-demo-msg {
        max-width: 100%;
    }

    .bq-section-header--split {
        flex-direction: column-reverse;
        align-items: flex-start;
        gap: 1.25rem;
    }

    .bq-why-bunny {
        width: 6.5rem;
        height: 6.5rem;
    }
}

@media (max-width: 500px) {
    .bq-hero h1 {
        font-size: 3rem;
    }

    .bq-reason p {
        padding-left: 0;
    }

    .bq-reason {
        flex-direction: column;
        gap: 0.5rem;
    }

    /* Stacked, there is no first line to sit level with. */
    .bq-reason .bq-icon {
        margin-top: 0;
    }
}
</style>
